/**
 * Die Bedienung eines Zuges — §67 Schritte 24 bis 35.
 *
 * **Das ist die zentrale Besonderheit des Spiels** (`CLAUDE.md`, Kernfunktionen Punkt 5): ein
 * gewählter Zug ist zunächst nur eine **öffentliche Erwägung**. Alle sehen sie live (§29), der
 * Spieler kann sie beliebig ändern (§31), und erst die Bestätigung macht sie verbindlich (§34).
 * Es gibt kein Undo.
 *
 * **Gewählt wird eine Action ID, nie eine Koordinate** (§74, Regel 1). Das Brett ist der bequeme
 * Weg dorthin — „Figur wählen, Feld klicken" —, aber hinausgeschickt wird die ID aus der Liste,
 * die die Engine berechnet hat. Der Server schlägt sie in **seiner** Liste nach (§110).
 *
 * **Die Liste der möglichen Züge steht neben dem Brett**, und zwar aus drei Gründen: sie ist der
 * vollständige, eindeutige Weg (das Brett zeigt nur Zielfelder), sie benennt die Aktionsart —
 * ziehen, schlagen, stoßen —, und sie macht die Action IDs sichtbar, was beim Balancing und im
 * Debug-Modus zählt. Sollte eine künftige Regeländerung zwei Aktionen auf dasselbe Feld legen,
 * ist sie außerdem die Stelle, an der man wählen kann (`game/turn.ts`).
 *
 * **`COMMIT` nennt, was bestätigt wird** — die ID der **Preview**, nicht die der lokal gewählten
 * Aktion. Beides ist normalerweise dasselbe; auseinander fällt es genau dann, wenn der Server
 * die Preview zwischenzeitlich verworfen hat. Dann soll der Klick nichts anderes ausführen als
 * das, was dasteht.
 *
 * **Bestätigen und Verwerfen sieht nur, wer erwägt.** Die Preview ist öffentlich, die Verfügung
 * über sie nicht (§40). Serverseitig ist das ohnehin geprüft — hier geht es darum, niemandem
 * einen Knopf zu zeigen, der für ihn nicht gilt.
 */

import { useTranslation } from 'react-i18next';

import type { ClientMessage } from '@flaghex/contracts';
import {
  cellId,
  type PieceType,
  type SectorId,
  type SectorLayout,
  type LegalAction,
} from '@flaghex/engine';

import { PieceGlyph } from '../board/PieceGlyph.js';
import type { PreviewView } from '../realtime/view.js';
import { ownedPieceName } from './ownedPiece.js';
import { actionsOfPiece, type TurnView } from './turn.js';

/** Was beschrieben werden kann: eine legale Aktion oder eine aufgelöste Preview. */
type DescribableAction = Pick<LegalAction, 'type' | 'to'> & {
  readonly piece: string;
  readonly victim?: string | undefined;
  readonly capturedByPush?: boolean | undefined;
  /** Wo die gestoßene Figur landet (B1, R5) — bei einem Stoß über den Rand nirgends. */
  readonly victimTo?: { readonly q: number; readonly r: number } | undefined;
};

export interface TurnControlsProps {
  readonly turn: TurnView;
  /** Die gewählte Figur — Brettfigur oder Vorratsfigur, derselbe Weg. */
  readonly selectedPiece: string | undefined;
  readonly onSelectPiece: (pieceId: string) => void;
  readonly preview: PreviewView;
  readonly me: string;
  /** Der eigene Sektor — der Vorrat zeigt die eigenen Figuren in der eigenen Farbe (R3). */
  readonly sector: SectorId;
  readonly layout: SectorLayout;
  /**
   * Die Figurenklasse je Figuren-ID — über **alle** Figuren der Partie, nicht nur die eigenen.
   *
   * Nötig, weil eine Preview auch eine fremde Figur nennt: „Anna schlägt deinen Läufer". Aus
   * `turn.placed` wäre das nicht auflösbar, denn dort stehen nur die eigenen — und dann zeigte
   * die Zeile eine interne Kennung (`p1-guard-1`) statt eines Namens. Genau so aufgefallen, von
   * einem Test über die **ganze** Zeile.
   */
  readonly pieceTypes: ReadonlyMap<string, PieceType>;
  /** Wem eine Figur gehört — gebraucht seit **R2** für die Stoßzeile (Befund B6). */
  readonly pieceOwners: ReadonlyMap<string, string>;
  /** Anzeigenamen aus der Lobby-Ansicht; fehlt einer, bleibt die Kennung stehen. */
  readonly names: ReadonlyMap<string, string>;
  readonly send: (message: ClientMessage) => string | undefined;
}

export function TurnControls({
  turn,
  selectedPiece,
  onSelectPiece,
  preview,
  me,
  sector,
  layout,
  pieceTypes,
  pieceOwners,
  names,
  send,
}: TurnControlsProps) {
  const { t } = useTranslation(['play', 'pieces', 'pieceShort']);

  const actions = selectedPiece === undefined ? [] : actionsOfPiece(turn, selectedPiece);
  const mine = preview !== null && preview.player === me;

  /**
   * Eine Aktion in Worten.
   *
   * Die Feld-ID ist die **kanonische** aus der Engine (`green-C3`) und keine zweite Benennung:
   * so steht sie in der Referenzgrafik, im Event-Log und ab V2 im KI-Kontext. Die Figurenklasse
   * kommt aus `pieceTypes` und damit aus der ganzen Partie — fehlt sie doch, bleibt die Kennung
   * stehen, weil eine Kennung verständlicher ist als eine Lücke.
   */
  const describe = (action: DescribableAction): string => {
    const key = action.type === 'PUSH' && action.capturedByPush === true ? 'PUSH_OFF' : action.type;
    const named = (pieceId: string | undefined): string => {
      const type = pieceId === undefined ? undefined : pieceTypes.get(pieceId);
      return type === undefined ? (pieceId ?? '') : t(`pieces:${type}`);
    };
    /** Das Opfer **mit seinem Besitzer** (B6) — seit R2 kann es die eigene Figur sein. */
    const owned = (pieceId: string | undefined): string => {
      const ownerId = pieceId === undefined ? undefined : pieceOwners.get(pieceId);
      return ownedPieceName(t, ownerId === undefined ? undefined : names.get(ownerId), named(pieceId));
    };
    return t(`play:action.${key}`, {
      piece: named(action.piece),
      victim: owned(action.victim),
      cell: cellId(layout, action.to),
      // **Beide Felder** (B1): `to` ist beim Stoß das Feld, das man auswählt — dort *steht* das
      // Opfer. Wohin es geschoben wird, ist die Auskunft, wegen der man die Zeile liest.
      target: action.victimTo === undefined ? '' : cellId(layout, action.victimTo as never),
    });
  };

  return (
    <>
      <h3>{t('play:preview')}</h3>
      {preview === null ? (
        <p role="status">{t('play:previewNone')}</p>
      ) : (
        <>
          <p role="status">
            {mine ? t('play:previewMine') : t('play:previewBy', { name: names.get(preview.player) ?? preview.player })}
            {' '}
            {describe(preview)}
          </p>
          {!mine ? null : (
            <p>
              <button
                type="button"
                data-commit={preview.id}
                onClick={() => send({ type: 'COMMIT', actionId: preview.id })}
              >
                {t('play:commit')}
              </button>{' '}
              <button type="button" onClick={() => send({ type: 'CANCEL_PREVIEW' })}>
                {t('play:cancel')}
              </button>
            </p>
          )}
          {!mine ? null : <p>{t('play:commitHint')}</p>}
        </>
      )}

      {!turn.active ? (
        <p>{t('play:waitHint')}</p>
      ) : (
        <>
          <h3>{t('play:actions')}</h3>
          <p>{t('play:selectHint')}</p>
          {selectedPiece === undefined ? (
            <p>{t('play:nothingSelected')}</p>
          ) : actions.length === 0 ? (
            <p>{t('play:noActions')}</p>
          ) : (
            <>
              <p>{t('play:actionsHint')}</p>
              <ul>
                {actions.map((action) => (
                  <li key={action.id}>
                    <button
                      type="button"
                      data-action-id={action.id}
                      onClick={() => send({ type: 'SET_PREVIEW', actionId: action.id })}
                    >
                      {describe(action)}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <h3>{t('play:pool')}</h3>
          {turn.pool.length === 0 ? (
            <p>{t('play:poolEmpty')}</p>
          ) : (
            <>
              <p>{t('play:poolHint')}</p>
              <ul>
                {turn.pool.map((piece) => (
                  <li key={piece.id}>
                    <button
                      type="button"
                      data-pool-piece={piece.id}
                      aria-pressed={selectedPiece === piece.id}
                      onClick={() => onSelectPiece(piece.id)}
                    >
                      {/*
                        Das Symbol **vor** dem Namen (Zyklus R3): es ersetzt nichts, es kommt
                        dazu. Dieselbe Zeichnung wie auf dem Brett, damit man die Figur nicht
                        zweimal lernen muss.
                      */}
                      <PieceGlyph type={piece.type} sector={sector} />
                      {t(`pieces:${piece.type}`)} ({t(`pieceShort:${piece.type}`)})
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </>
  );
}
